import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import { usePricing } from '../contexts/PricingContext';
import TierBadge from './TierBadge';
import CompanySwitcher from './CompanySwitcher';
import GracePeriodBanner from './GracePeriodBanner';
import { toast } from 'sonner';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

// BUILD ID - Update this with every code change
export const BUILD_ID = "20261012.2220";

import { 
  Home, 
  TrendingUp, 
  MessageSquare, 
  HelpCircle, 
  PlayCircle,
  FileText,
  LogOut,
  Settings,
  Users,
  UserCog,
  User,
  ShieldCheck,
  ClipboardCheck,
  Tags,
  ChevronDown, ChevronRight,
  Mail,
  AlertTriangle,
  ArrowLeftCircle,
  Palette,
  Menu,
  X,
  HardDrive,
  LayoutDashboard,
  Shield,
  FolderOpen,
  Building2,
  CreditCard,
  FileCheck,
  CheckSquare,
  BarChart3,
  BookTemplate,
  UserPlus,
  Ticket,
  FileQuestion,
  Globe,
  Table2,
  Award,
  Brain,
  Cpu,
  Bot,
  Maximize2,
  Minimize2,
  Scale,
  Layers,
  ListTodo,
  Share2,
  Send
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import DebugPanel from './DebugPanel';

// Trados brand colors (fallback)
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';
const BRAND_YELLOW = '#FFD166';

// Icon mapping for dynamic sidebar - maps string names to icon components
const iconMap = {
  Home, 
  LayoutDashboard,
  Users, 
  TrendingUp, 
  MessageSquare, 
  HelpCircle, 
  PlayCircle, 
  FileText, 
  ClipboardCheck, 
  Tags, 
  Mail, 
  Palette, 
  UserCog, 
  Settings, 
  HardDrive, 
  ShieldCheck,
  Shield,
  FolderOpen,
  Building2,
  CreditCard,
  FileCheck,
  CheckSquare,
  BarChart3,
  BookTemplate,
  UserPlus,
  Ticket,
  FileQuestion,
  Globe,
  Table2,
  Award,
  Brain,
  Cpu,
  Bot,
  Scale,
  Layers,
  ListTodo,
  Share2,
  Send
};

const Sidebar = ({ children }) => {
  const { user, logout, isImpersonating, returnToAdmin } = useAuth();
  const { enforced: pricingEnforced, billing } = usePricing();
  const hasLiveSubscription = ['active', 'trialing'].includes(billing?.subscription_status);
  const { adminTheme, userTheme } = useTheme();
  const { company: subdomainCompany, isSubdomain } = useSubdomain();
  const location = useLocation();
  const navigate = useNavigate();
  // Check if user is any type of admin
  const isAdmin = user?.role && ['admin', 'main_admin', 'company_admin'].includes(user.role);
  const [tierStatus, setTierStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOrder, setSidebarOrder] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [securityPermissions, setSecurityPermissions] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sidebar_collapsed_cats') || '{}');
    } catch { return {}; }
  });
  const [zenMode, setZenMode] = useState(false);
  
  // Check if user is company admin (to use company branding)
  const isCompanyAdmin = user?.role === 'company_admin';
  
  // Check if user belongs to a company (either as admin or regular user on subdomain)
  const isCompanyUser = isCompanyAdmin || (isSubdomain && subdomainCompany);
  
  // Get the appropriate theme based on user role
  const theme = isAdmin ? adminTheme : userTheme;
  
  // Get company theme settings - use fetched settings for company admin, or subdomain company for regular users
  const activeCompanySettings = isCompanyAdmin ? companySettings : (isSubdomain ? subdomainCompany : null);
  
  // For company users (admin or regular users on subdomain), use company theme settings if available
  const sidebarStyles = activeCompanySettings?.theme_settings
    ? {
        backgroundColor: activeCompanySettings.theme_settings.sidebar_bg || theme?.sidebar?.backgroundColor,
        textColor: activeCompanySettings.theme_settings.sidebar_text || theme?.sidebar?.textColor,
        hoverColor: activeCompanySettings.theme_settings.sidebar_link_hover || theme?.sidebar?.hoverColor,
        activeColor: activeCompanySettings.theme_settings.sidebar_link_active || theme?.sidebar?.activeColor,
        logoUrl: activeCompanySettings.logo_url || theme?.sidebar?.logoUrl,
        siteTitle: activeCompanySettings.name || theme?.sidebar?.siteTitle
      }
    : (theme?.sidebar || {});
  
  // For company users, override logo with company branding logo
  const effectiveLogoUrl = activeCompanySettings?.logo_url 
    ? activeCompanySettings.logo_url 
    : sidebarStyles.logoUrl;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Fetch company settings for company admins (to get branding logo)
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const res = await api.get('/company/settings');
        setCompanySettings(res.data);
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      }
    };
    
    if (user && isCompanyAdmin) {
      fetchCompanySettings();
    }
  }, [user, isCompanyAdmin]);

  // Fetch security permissions for company admins
  useEffect(() => {
    const fetchSecurityPermissions = async () => {
      try {
        const res = await api.get('/settings/security-permissions');
        setSecurityPermissions(res.data);
      } catch (error) {
        console.error('Failed to fetch security permissions:', error);
      }
    };
    
    if (user && isCompanyAdmin) {
      fetchSecurityPermissions();
    }
  }, [user, isCompanyAdmin]);

  const handleReturnToAdmin = async () => {
    const success = await returnToAdmin();
    if (success) {
      toast.success('Returned to admin account');
      navigate('/admin');
    } else {
      toast.error('Failed to return to admin');
    }
  };

  useEffect(() => {
    const fetchTierStatus = async () => {
      try {
        const res = await api.get('/tiers/my-status');
        setTierStatus(res.data);
      } catch (error) {
        console.error('Failed to fetch tier status:', error);
      }
    };
    
    if (user && !isAdmin) {
      fetchTierStatus();
    }
  }, [user, isAdmin]);

  // Fetch saved sidebar order
  useEffect(() => {
    const fetchSidebarOrder = async () => {
      try {
        if (isAdmin) {
          const res = await api.get('/admin/settings/sidebar-order');
          setSidebarOrder(res.data);
        } else {
          const res = await api.get('/settings/sidebar-order');
          setSidebarOrder(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch sidebar order:', error);
      }
    };
    
    if (user) {
      fetchSidebarOrder();
    }
  }, [user, isAdmin]);

  // Default nav items (fallback)
  const defaultUserNavItems = [
    { id: 'dashboard', path: '/admin/dashboard', icon: 'Home', label: 'Dashboard', visible: true },
    { id: 'request-quote', path: '/admin/requests/new', icon: 'FileText', label: 'Request a Quote', visible: true },
    { id: 'my-requests', path: '/admin/requests', icon: 'ClipboardCheck', label: 'View My Requests', visible: true },
    { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
    { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'My Created LOIs', visible: true },
    { id: 'edit-business-info', path: '/admin/business-info', icon: 'Building2', label: 'Edit Business Info', visible: true },
    { id: 'network-badge', path: '/admin/network-badge', icon: 'Award', label: 'Trados Network Badge', visible: true },
    { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
    { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'My Verified LOIs', visible: true },
    { id: 'directory', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
    { id: 'my-company-listing', path: '/admin/my-company-listing', icon: 'Building2', label: 'My Company Listing', visible: true },
    { id: 'opportunities', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
    { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
    { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support Ticket', visible: true },
    { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Educational Videos', visible: true },
    { id: 'my-referrals', path: '/admin/my-referrals', icon: 'Users', label: 'My Referrals', visible: true },
    { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
    { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite Users', visible: true },
    { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Selling Process', visible: true },
  ];

  // Check if user is main admin (vs company admin)
  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  // Main Admin nav items (full platform access)
  const mainAdminNavItems = [
    { id: 'dashboard', path: '/admin', icon: 'Home', label: 'Dashboard', visible: true },
    // Multi-Tenant Management (Main Admin Only)
    { id: 'companies', path: '/admin/companies', icon: 'Building2', label: 'Companies', visible: true },
    { id: 'applications', path: '/admin/applications', icon: 'FileCheck', label: 'Applications', visible: true },
    { id: 'membership-plans', path: '/admin/membership-plans', icon: 'CreditCard', label: 'Membership Plans', visible: true },
    { id: 'coupons', path: '/admin/coupons', icon: 'Ticket', label: 'Coupon Codes', visible: true },
    { id: 'company-approval', path: '/admin/company-approval-steps', icon: 'ClipboardCheck', label: 'Approval Setup', visible: true },
    { id: 'company-verifications', path: '/admin/company-verifications', icon: 'CheckSquare', label: 'Verifications', visible: true },
    { id: 'platform-analytics', path: '/admin/platform-analytics', icon: 'TrendingUp', label: 'Platform Analytics', visible: true },
    { id: 'email-leads', path: '/admin/leads', icon: 'Mail', label: 'Email Leads', visible: true },
    { id: 'trados-agent', path: '/admin/trados-agent', icon: 'Bot', label: 'Trados Agent', visible: true },
    // Standard Admin
    { id: 'users', path: '/admin/users', icon: 'Users', label: 'Manage Users', visible: true },
    { id: 'tier-approvals', path: '/admin/tier-approvals', icon: 'ClipboardCheck', label: 'Tier Approvals', visible: true },
    { id: 'tier-questions', path: '/admin/tier-questions', icon: 'Shield', label: 'Tier Questions', visible: true },
    { id: 'account-types', path: '/admin/account-types', icon: 'Tags', label: 'Account Types', visible: true },
    { id: 'deals', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
    { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
    { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support', visible: true },
    { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Videos', visible: true },
    { id: 'quote-requests', path: '/admin/quote-requests', icon: 'FileText', label: 'Quote Requests', visible: true },
    { id: 'process-steps', path: '/admin/process-steps', icon: 'ClipboardCheck', label: 'Quote Steps', visible: true },
    { id: 'process-templates', path: '/admin/process-templates', icon: 'BookTemplate', label: 'Steps Admin MGMT', visible: true },
    { id: 'question-templates', path: '/admin/question-templates', icon: 'FileQuestion', label: 'Questions Admin MGMT', visible: true },
    { id: 'company-questions', path: '/admin/company-questions', icon: 'UserCog', label: 'Quote Questions', visible: true },
    { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
    { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'Created LOIs', visible: true },
    { id: 'all-created-lois', path: '/admin/created-lois', icon: 'Files', label: 'Created LOIs via Trados', visible: true },
    { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
    { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'All LOIs Verifications', visible: true },
    { id: 'documents', path: '/admin/documents', icon: 'FolderOpen', label: 'Documents', visible: true },
    { id: 'logs', path: '/admin/logs', icon: 'Mail', label: 'Logs', visible: true },
    { id: 'upload-settings', path: '/admin/upload-settings', icon: 'HardDrive', label: 'Upload Settings', visible: true },
    { id: 'theme', path: '/admin/theme', icon: 'Palette', label: 'Theme', visible: true },
    { id: 'theme-presets', path: '/admin/theme-presets', icon: 'Palette', label: 'Theme Presets', visible: true },
    { id: 'public-theme', path: '/admin/public-theme', icon: 'Palette', label: 'Public Theme', visible: true },
    { id: 'settings', path: '/admin/settings', icon: 'Settings', label: 'Settings', visible: true },
    // Main Admin Platform Settings
    { id: 'security-settings', path: '/admin/security-settings', icon: 'Shield', label: 'Security', visible: true },
    { id: 'stripe-settings', path: '/admin/stripe-settings', icon: 'CreditCard', label: 'Stripe', visible: true },
    { id: 'membership-settings', path: '/admin/membership-settings', icon: 'Shield', label: 'Membership Settings', visible: true },
    { id: 'financial', path: '/admin/financial', icon: 'BarChart3', label: 'Financial', visible: true },
    { id: 'commodities', path: '/admin/commodities', icon: 'TrendingUp', label: 'Commodity Tracker', visible: true },
    { id: 'directory-public', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
    { id: 'directory', path: '/admin/directory', icon: 'Globe', label: 'Manage Directory', visible: true },
    { id: 'directory-data', path: '/admin/cd', icon: 'Table2', label: 'Directory Data', visible: true },
    { id: 'integration-settings', path: '/admin/integration-settings', icon: 'Globe', label: 'API & Integrations', visible: true },
    { id: 'analytics-scripts', path: '/admin/analytics', icon: 'BarChart3', label: 'Analytics', visible: true },
    { id: 'referral-tree', path: '/admin/referral-tree', icon: 'Users', label: 'Referrals', visible: true },
    { id: 'global-users', path: '/admin/global-users', icon: 'Globe', label: 'Global Users', visible: true },
    { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
    { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite', visible: true },
    { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Process', visible: true },
    // External Services
    { id: 'external-services', path: '/admin/external-services', icon: 'Globe', label: 'External Services', visible: true },
    // Intel AI Settings
    { id: 'intel-settings', path: '/admin/intel-settings', icon: 'Brain', label: 'Intel Settings', visible: true },
    // Email Templates
    { id: 'email-templates', path: '/admin/email-templates', icon: 'Mail', label: 'Email Templates', visible: true },
    // Newsletters
    { id: 'newsletters', path: '/admin/newsletters', icon: 'Send', label: 'Newsletters', visible: true },
    // Posting (automated news & offers)
    { id: 'posting', path: '/admin/posting', icon: 'FilePlus', label: 'Posting', visible: true },
    // Legal
    { id: 'disclaimer', path: '/admin/disclaimer', icon: 'Scale', label: 'Legal Disclaimer', visible: true },
    // Dashboard Control
    { id: 'dashboard-control', path: '/admin/dashboard-control', icon: 'LayoutDashboard', label: 'Dashboard Control', visible: true },
  ];

  // Company Admin nav items (restricted to company-scoped features)
  const companyAdminNavItems = [
    { id: 'dashboard', path: '/admin', icon: 'Home', label: 'Dashboard', visible: true },
    // Company Management
    { id: 'company-settings', path: '/admin/company-settings', icon: 'Building2', label: 'Company Settings', visible: true },
    { id: 'edit-business-info', path: '/admin/business-info', icon: 'Building2', label: 'Edit Business Info', visible: true },
    { id: 'network-badge', path: '/admin/network-badge', icon: 'Award', label: 'Trados Network Badge', visible: true },
    { id: 'billing', path: '/admin/billing', icon: 'CreditCard', label: 'Billing', visible: true },
    { id: 'users', path: '/admin/users', icon: 'Users', label: 'Manage Users', visible: true },
    { id: 'account-types', path: '/admin/account-types', icon: 'Tags', label: 'Account Types', visible: true },
    // Content Management
    { id: 'directory', path: '/directory', icon: 'Globe', label: 'Company Directory', visible: true },
    { id: 'deals', path: '/admin/deals', icon: 'TrendingUp', label: 'Opportunities', visible: true },
    { id: 'announcements', path: '/admin/announcements', icon: 'MessageSquare', label: 'Announcements', visible: true },
    { id: 'support', path: '/admin/support', icon: 'HelpCircle', label: 'Support', visible: true },
    { id: 'videos', path: '/admin/videos', icon: 'PlayCircle', label: 'Videos', visible: true },
    // Question Configuration - Single unified page
    { id: 'company-questions', path: '/admin/company-questions', icon: 'FileQuestion', label: 'Quote Question MGMT', visible: true },
    // Quote Process Configuration
    { id: 'quote-requests', path: '/admin/quote-requests', icon: 'FileText', label: 'View Quote Request', visible: true },
    { id: 'process-steps', path: '/admin/process-steps', icon: 'ClipboardCheck', label: 'Quote Steps', visible: true },
    // LOI Creation & Verification
    { id: 'create-loi', path: '/create-loi', icon: 'FilePlus', label: 'Create LOI', visible: true },
    { id: 'my-created-lois', path: '/admin/my-created-lois', icon: 'Files', label: 'Created LOIs', visible: true },
    { id: 'verify-loi', path: '/admin/verify-loi-internal', icon: 'FileCheck', label: 'Verify LOIs', visible: true },
    { id: 'my-verified-lois', path: '/admin/my-verified-lois', icon: 'FileCheck', label: 'Verified LOIs', visible: true },
    // Invites
    { id: 'access-requests', path: '/admin/access-requests', icon: 'Globe', label: 'Access Requests', visible: true },
    { id: 'my-tasks', path: '/admin/my-tasks', icon: 'ListTodo', label: 'My Tasks', visible: true },
    { id: 'invite-users', path: '/admin/invite', icon: 'UserPlus', label: 'Invite Users', visible: true },
    { id: 'share-selling-process', path: '/admin/share-selling-process', icon: 'Share2', label: 'Share Selling Process', visible: true },
  ];

  const defaultAdminNavItems = isMainAdmin ? mainAdminNavItems : companyAdminNavItems;

  // Get nav items based on saved order or defaults, merging in any new default items
  const getNavItems = () => {
    const mergeNewItems = (savedOrder, defaultItems) => {
      if (!savedOrder || savedOrder.length === 0) {
        return defaultItems;
      }
      
      // Find items in defaults that aren't in saved order (new items)
      const savedIds = new Set(savedOrder.map(item => item.id));
      const newItems = defaultItems.filter(item => !savedIds.has(item.id));
      
      // Merge: saved order + new items at the end
      const merged = [...savedOrder];
      if (newItems.length > 0) {
        merged.push(...newItems);
      }
      
      return merged.filter(item => item.visible !== false);
    };

    if (isAdmin) {
      // For company admins, use saved company_admin_links order
      if (isCompanyAdmin) {
        // Apply security permission filters for company admin
        let baseItems = sidebarOrder?.company_admin_links?.length > 0 
          ? mergeNewItems(sidebarOrder.company_admin_links, companyAdminNavItems)
          : [...companyAdminNavItems];
        
        // Hide Account Types if permission is disabled
        if (securityPermissions && securityPermissions.company_admin_manage_account_types === false) {
          baseItems = baseItems.filter(item => item.id !== 'account-types');
        }

        // While the platform is free there is nothing to buy, so Billing only
        // stays for companies that still carry a live subscription to manage.
        if (!pricingEnforced && !hasLiveSubscription) {
          baseItems = baseItems.filter(item => item.id !== 'billing');
        }

        return baseItems;
      }
      // For main admins, use saved order
      return mergeNewItems(sidebarOrder?.admin_links, mainAdminNavItems);
    } else {
      return mergeNewItems(sidebarOrder?.user_links, defaultUserNavItems);
    }
  };

  const navItems = getNavItems();

  const toggleSidebarCategory = (catId) => {
    setCollapsedCategories(prev => {
      // Default is collapsed (true), so toggle: if not explicitly false, set to false (expand); otherwise set to true (collapse)
      const currentlyCollapsed = prev[catId] !== false;
      const updated = { ...prev, [catId]: currentlyCollapsed ? false : true };
      localStorage.setItem('sidebar_collapsed_cats', JSON.stringify(updated));
      return updated;
    });
  };

  // Build grouped nav structure for admin sidebars
  const getGroupedNavItems = () => {
    const categories = navItems.filter(item => item.type === 'parent');
    const ungrouped = navItems.filter(item => item.type !== 'parent' && !item.parent_id);
    const grouped = {};
    categories.forEach(cat => {
      grouped[cat.id] = navItems.filter(item => item.parent_id === cat.id);
    });
    return { categories, ungrouped, grouped };
  };

  const hasCategories = isAdmin && navItems.some(item => item.type === 'parent');

  const isActive = (path) => {
    if (!path || typeof path !== 'string') return false;
    // Normalize so highlighting works whether the app is served at root
    // (legacy) or under /admin (domain-consolidation cutover). '/admin'
    // itself (admin home) is kept as-is; '/admin/x' collapses to '/x'.
    const norm = (p) => {
      if (typeof p !== 'string') return '';
      return p === '/admin' ? '/admin' : (p.startsWith('/admin/') ? p.slice('/admin'.length) : p);
    };
    const here = norm(location.pathname);
    const target = norm(path);
    // Exact match for dashboard pages
    if (target === '/dashboard' || target === '/admin') {
      return here === target;
    }
    // Exact match for specific paths to avoid prefix matching issues
    if (target === '/requests/new') {
      return here === '/requests/new';
    }
    if (target === '/requests') {
      return here === '/requests' || (here.startsWith('/requests/') && here !== '/requests/new');
    }
    // Match exact path or path followed by '/' (proper segment boundary)
    const matches = here === target || here.startsWith(target + '/');
    if (!matches) return false;
    // If another nav item has a longer (more specific) matching path, don't highlight this one
    const hasMoreSpecificMatch = navItems.some(item =>
      item.path && norm(item.path) !== target &&
      norm(item.path).length > target.length &&
      (here === norm(item.path) || here.startsWith(norm(item.path) + '/'))
    );
    return !hasMoreSpecificMatch;
  };

  // Set page title based on current page
  useEffect(() => {
    const siteTitle = theme?.sidebar?.siteTitle || 'Trados — The Trusted Trade Platform';
    
    // Find the active nav item to get the page name
    const activeItem = navItems.find(item => isActive(item.path));
    const pageName = activeItem?.label || '';
    
    if (pageName) {
      document.title = `${pageName} | ${siteTitle}`;
    } else {
      document.title = siteTitle;
    }
  }, [location.pathname, navItems, theme]);

  const getUserDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user?.email?.split('@')[0] || 'User';
  };

  const getProfilePhoto = () => {
    if (user?.profile_photo) {
      return resolveFileUrl(user.profile_photo, process.env.REACT_APP_BACKEND_URL || '');
    }
    return null;
  };

  // Check if user needs to verify email
  const needsEmailVerification = user && !isAdmin && user.email_verified === false;

  // Sidebar content component (reused for both desktop and mobile)
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <Link to={isAdmin ? '/admin' : '/admin/dashboard'} className="block" onClick={() => setMobileMenuOpen(false)}>
          {/* Custom Logo or Default Trados Logo Icon */}
          {effectiveLogoUrl ? (
            <img 
              src={resolveFileUrl(effectiveLogoUrl, process.env.REACT_APP_BACKEND_URL || '')}
              alt="Logo" 
              className="w-full h-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="flex items-center justify-center gap-3 py-2">
              <div className="w-10 h-10 flex flex-col justify-center items-center gap-0.5">
                <div className="w-6 h-1.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
                <div className="w-7 h-1.5 rounded-full" style={{ backgroundColor: BRAND_ORANGE }}></div>
                <div className="w-8 h-1.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
              </div>
              <div>
                <span className="font-bold text-xl tracking-wide">TRADOS</span>
                {isAdmin && <span className="text-xs opacity-60 block">Admin Panel</span>}
              </div>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {/* Company Switcher - Only shows when user has multiple companies */}
        <CompanySwitcher />

        <ul className="space-y-0.5">
          {(() => {
            const renderNavLink = (item) => {
              const Icon = typeof item.icon === 'string' ? (iconMap[item.icon] || Home) : (item.icon || Home);
              const active = isActive(item.path);
              return (
                <li key={item.id || item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-1.5 rounded-lg transition-all"
                    style={{
                      backgroundColor: active
                        ? (sidebarStyles.activeColor || 'rgba(255,255,255,0.2)')
                        : 'transparent',
                      color: sidebarStyles.textColor || '#FFFFFF',
                      opacity: active ? 1 : 0.7
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = sidebarStyles.hoverColor || 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.opacity = '0.7';
                      }
                    }}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </Link>
                </li>
              );
            };

            if (hasCategories) {
              const { categories, ungrouped, grouped } = getGroupedNavItems();
              return (
                <>
                  {ungrouped.map(renderNavLink)}
                  {categories.map((cat) => {
                    const children = grouped[cat.id] || [];
                    if (children.length === 0) return null;
                    const isCollapsed = collapsedCategories[cat.id] !== false;
                    const showChildren = !isCollapsed;
                    return (
                      <li key={cat.id}>
                        <button
                          onClick={() => toggleSidebarCategory(cat.id)}
                          className="flex items-center gap-2 w-full px-4 py-2 mt-3 mb-1 rounded-lg transition-all"
                          style={{
                            color: sidebarStyles.textColor || '#FFFFFF',
                            opacity: 0.5
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                        >
                          {showChildren
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />
                          }
                          <span className="text-xs font-semibold uppercase tracking-wider">{cat.label}</span>
                        </button>
                        {showChildren && (
                          <ul className="space-y-0.5">
                            {children.map(renderNavLink)}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </>
              );
            }

            return navItems.map(renderNavLink);
          })()}
        </ul>

        {/* User Tier Upgrade Section */}
        {!isAdmin && tierStatus && (() => {
          // Compute the effective approved tier and pending tier for sidebar display
          const prevTierMap = { silver: 'bronze', gold: 'silver' };
          const currentTier = tierStatus.tier || 'none';
          const currentStatus = tierStatus.tier_status || 'not_started';

          // Use approved_tier from API, or infer: if pending a higher tier, previous tier must be approved
          let effectiveApprovedTier = tierStatus.approved_tier && tierStatus.approved_tier !== 'none'
            ? tierStatus.approved_tier
            : (currentStatus === 'pending' && prevTierMap[currentTier])
              ? prevTierMap[currentTier]
              : 'none';

          // If current tier is approved, that IS the approved tier
          if (currentStatus === 'approved' && currentTier !== 'none') {
            effectiveApprovedTier = currentTier;
          }

          // Determine what badge to show
          const badgeTier = effectiveApprovedTier !== 'none' ? effectiveApprovedTier : currentTier;
          const badgeStatus = effectiveApprovedTier !== 'none' ? 'approved' : currentStatus;

          // Show pending text only when pending a HIGHER tier than the approved one
          const pendingHigherTier = currentStatus === 'pending' && effectiveApprovedTier !== 'none' && currentTier !== effectiveApprovedTier;

          return (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="px-2 mb-2">
              <span className="text-xs text-white/50 uppercase tracking-wide">Verification</span>
            </div>

            {/* Current Tier */}
            <div className="px-2 py-2 mb-2 flex flex-col items-center w-full">
              <div className="w-full">
                <TierBadge
                  tier={badgeTier}
                  tierStatus={badgeStatus}
                  size="lg"
                  fullWidth
                />
              </div>
              {pendingHigherTier && (
                <div className="mt-1.5 text-center">
                  <span className="text-xs text-amber-400 animate-pulse">
                    Pending {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Upgrade Links */}
            {tierStatus.can_submit_bronze && (
              <Link
                to="/admin/verify/bronze"
                data-testid="nav-verify-bronze"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-1.5 rounded-lg hover:bg-white/10 transition-all"
                style={{ color: BRAND_YELLOW }}
              >
                <ShieldCheck className="w-5 h-5" />
                <span className="font-medium text-sm">Get Bronze</span>
              </Link>
            )}
            {tierStatus.can_submit_silver && (
              <Link
                to="/admin/verify/silver"
                data-testid="nav-verify-silver"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-1.5 rounded-lg text-white/80 hover:bg-white/10 transition-all"
              >
                <ShieldCheck className="w-5 h-5" />
                <span className="font-medium text-sm">Get Silver</span>
              </Link>
            )}
            {tierStatus.can_submit_gold && (
              <Link
                to="/admin/verify/gold"
                data-testid="nav-verify-gold"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-1.5 rounded-lg hover:bg-white/10 transition-all"
                style={{ color: BRAND_YELLOW }}
              >
                <ShieldCheck className="w-5 h-5" />
                <span className="font-medium text-sm">Get Gold</span>
              </Link>
            )}
          </div>
          );
        })()}
      </nav>

      {/* Mobile User Actions */}
      <div className="lg:hidden p-4 border-t border-white/10">
        {/* Profile Photo + Name */}
        <div className="flex items-center gap-3 px-4 py-2 mb-2">
          {getProfilePhoto() ? (
            <img 
              src={getProfilePhoto()} 
              alt="Profile" 
              className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-5 h-5 text-white/70" />
            </div>
          )}
          <div>
            <p className="font-medium text-white text-sm">{getUserDisplayName()}</p>
            <p className="text-white/60 text-xs truncate max-w-[150px]">{user?.email}</p>
          </div>
        </div>
        <div className="space-y-1">
          <Link
            to="/admin/profile/edit"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-2 rounded-lg text-white/80 hover:bg-white/10 transition-all"
          >
            <User className="w-5 h-5" />
            <span className="font-medium text-sm">Edit Profile</span>
          </Link>
          <Link
            to="/admin/company-settings"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-2 rounded-lg text-white/80 hover:bg-white/10 transition-all"
          >
            <Building2 className="w-5 h-5" />
            <span className="font-medium text-sm">Edit Business</span>
          </Link>
          
          <button 
            onClick={() => {
              setMobileMenuOpen(false);
              logout();
            }}
            className="flex items-center gap-3 px-4 py-1.5 rounded-lg text-white/80 hover:bg-white/10 transition-all w-full"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </div>
    </>
  );

  // Zen Mode (full screen) - hide sidebar, header, footer
  if (zenMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Floating return button */}
        <button
          onClick={() => setZenMode(false)}
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 hover:shadow-xl transition-all text-sm font-medium group"
          title="Exit full screen"
        >
          <Minimize2 className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
          <span className="hidden sm:inline">Exit Full Screen</span>
        </button>
        <main className="main-content">
          <GracePeriodBanner />
          {children}
          <DebugPanel />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Mobile Header */}
      <header 
        className="lg:hidden fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between border-b border-gray-200"
        style={{ backgroundColor: sidebarStyles.backgroundColor || BRAND_BLUE }}
      >
        {/* Logo for mobile header */}
        <Link to={isAdmin ? '/admin' : '/admin/dashboard'} className="flex items-center gap-2">
          {sidebarStyles.logoUrl ? (
            <img 
              src={resolveFileUrl(sidebarStyles.logoUrl, process.env.REACT_APP_BACKEND_URL || '')}
              alt="Logo" 
              className="h-8 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <>
              <div className="w-8 h-8 flex flex-col justify-center items-center gap-0.5">
                <div className="w-4 h-1 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
                <div className="w-5 h-1 rounded-full" style={{ backgroundColor: BRAND_ORANGE }}></div>
                <div className="w-6 h-1 rounded-full" style={{ backgroundColor: BRAND_YELLOW }}></div>
              </div>
              <span className="font-bold text-lg text-white tracking-wide">TRADOS</span>
            </>
          )}
        </Link>

        {/* Mobile Header Actions */}
        <div className="flex items-center gap-2">
          {/* Full Screen Toggle for Mobile */}
          <button
            onClick={() => setZenMode(true)}
            className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            title="Full screen mode"
          >
            <Maximize2 className="w-5 h-5" />
          </button>

          {/* Notification Bell for Mobile */}
          <div className="text-white">
            <NotificationBell />
          </div>
          
          {/* Hamburger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside 
        className={`lg:hidden fixed top-0 left-0 h-full w-72 z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ 
          backgroundColor: sidebarStyles.backgroundColor || BRAND_BLUE,
          color: sidebarStyles.textColor || '#FFFFFF'
        }}
      >
        {/* Close button inside drawer */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarContent />
      </aside>

      {/* Desktop Sidebar */}
      <aside 
        className="hidden lg:flex w-64 flex-col fixed h-full z-40"
        style={{ 
          backgroundColor: sidebarStyles.backgroundColor || BRAND_BLUE,
          color: sidebarStyles.textColor || '#FFFFFF'
        }}
      >
        <SidebarContent />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0 overflow-x-hidden">
        {/* Top Navigation Bar - Desktop only */}
        <header className="hidden lg:flex bg-white border-b border-gray-200 px-8 py-4 items-center justify-end sticky top-0 z-30">
          <div className="flex items-center gap-6">
            {/* Profile Photo + Welcome Message */}
            <div className="flex items-center gap-3">
              {getProfilePhoto() ? (
                <img 
                  src={getProfilePhoto()} 
                  alt="Profile" 
                  className="w-9 h-9 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
              )}
              <span className="text-gray-700 text-sm">
                Welcome, <span className="font-medium">{getUserDisplayName()}</span>
              </span>
            </div>
            
            {/* Divider */}
            <div className="h-6 w-px bg-gray-200"></div>
            
            {/* Return to Admin Button - only shown when impersonating */}
            {isImpersonating && (
              <>
                <button 
                  onClick={handleReturnToAdmin}
                  data-testid="return-to-admin-btn"
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 text-sm font-medium transition"
                >
                  <ArrowLeftCircle className="w-4 h-4" />
                  Return to Admin
                </button>
                <div className="h-6 w-px bg-gray-200"></div>
              </>
            )}
            
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* Language Selector */}
            <LanguageSelector variant="header" />
            
            {/* Full Screen Toggle */}
            <button
              onClick={() => setZenMode(true)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition text-sm font-medium"
              title="Full screen mode"
            >
              <Maximize2 className="w-4 h-4" />
              <span>Full Screen</span>
            </button>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-200"></div>

            {/* Profile Link */}
            <Link
              to="/admin/profile/edit"
              data-testid="top-nav-profile"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium transition"
            >
              Profile
            </Link>

            {/* Business Link */}
            <Link
              to="/admin/company-settings"
              data-testid="top-nav-business"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium transition"
            >
              Business
            </Link>

            {/* Logout */}
            <button 
              onClick={logout}
              data-testid="top-nav-logout"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium transition"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Spacer for mobile header */}
        <div className="lg:hidden h-14"></div>

        {/* Email Verification Banner */}
        {needsEmailVerification && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-8 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span className="text-amber-800 text-sm">
                  Your email is not verified. Please check your inbox.
                </span>
              </div>
              <button 
                onClick={async () => {
                  try {
                    await api.post('/auth/resend-verification');
                    toast.success('Verification email sent! Check your inbox.');
                  } catch (e) {
                    toast.error('Failed to send verification email');
                  }
                }}
                className="text-amber-700 hover:text-amber-900 text-sm font-medium underline sm:ml-auto"
              >
                Resend Email
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col main-content min-w-0">
          {children}
          <DebugPanel />
        </main>

        {/* Powered by Footer */}
        {adminTheme?.footer?.poweredByText && (
          <footer className="py-3 px-8 text-right border-t border-gray-100 bg-white">
            <a 
              href={adminTheme?.footer?.poweredByUrl || 'https://www.trados.co'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-700 transition"
            >
              {adminTheme?.footer?.poweredByText}
            </a>
          </footer>
        )}
        
        {/* Build ID */}
        <div className="py-2 px-4 text-center border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-400 font-mono">Build: {BUILD_ID}</span>
        </div>
      </div>

    </div>
  );
};

export default Sidebar;
